import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { GenerateButton } from "@/components/shared";
import { toast } from "sonner";
import { Check, X, Star, Pin, Trash2, Plus, Pencil, MapPin, Loader2, Archive, History, Flag, RotateCcw, Download, Upload, FileSpreadsheet, Image as ImageIcon, GripVertical, BarChart3, Eye, Phone, Globe, Navigation } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const photoUrl = (p) => (p && p.startsWith("http") ? p : `${BACKEND}/api/afromap/photo/${p}`);
const inputCls = "w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-gold/40";
const EMPTY = { name: "", category: "restaurant", description: "", address: "", city: "", postal_code: "", country: "FR", phone: "", email: "", website: "", instagram: "", facebook: "", hours: "", tags: "", event: "", lat: "", lng: "" };

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="card-surface p-3.5 rounded-xl">
    <div className="flex items-center gap-2 text-white/50 text-xs"><Icon size={14} className="text-gold" /> {label}</div>
    <p className="text-2xl font-display mt-1">{(value || 0).toLocaleString("fr-FR")}</p>
  </div>
);

export default function AdminAfroMap() {
  const [view, setView] = useState("places"); // places | reviews
  const [status, setStatus] = useState("pending");
  const [places, setPlaces] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [editing, setEditing] = useState(null);
  const [historyId, setHistoryId] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [photoReports, setPhotoReports] = useState([]);
  const [dash, setDash] = useState(null);
  const [importing, setImporting] = useState(false);
  const [geo, setGeo] = useState({ running: false, remaining: 0 });

  const catLabel = (k) => categories.find((c) => c.key === k)?.label || k;

  const downloadCsv = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/afromap/admin/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import terminé : ${data.created} créés, ${data.updated} mis à jour (${data.total} lignes).`);
      if (data.note) toast.warning(data.note);
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get(`/afromap/admin/places?status=${status}`),
        api.get(`/afromap/admin/stats`),
      ]);
      setPlaces(p.data.places || []); setStats(s.data || {});
    } catch (e) { toast.error(apiErr(e)); }
  }, [status]);

  const pollGeo = useCallback(async () => {
    try {
      const { data } = await api.get("/afromap/admin/geocode-status");
      setGeo(data);
      if (data.running) setTimeout(pollGeo, 2500);
      else if (data.total) { toast.success(`Géocodage terminé : ${data.geocoded} géolocalisés, ${data.failed} échec(s).`); load(); }
    } catch { setTimeout(pollGeo, 4000); }
  }, [load]);

  const geocodeMissing = async () => {
    try {
      const { data } = await api.post("/afromap/admin/geocode-missing");
      if (data.started === false && !data.already_running) { toast.message(data.message || "Rien à géolocaliser."); return; }
      toast.success(`Géocodage lancé (${data.total || geo.remaining} lieux). Cela peut prendre quelques minutes.`);
      pollGeo();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const loadReviews = useCallback(async () => {
    try { const { data } = await api.get(`/afromap/admin/reviews?status=reported`); setReviews(data.reviews || []); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);

  const loadPhotoReports = useCallback(async () => {
    try { const { data } = await api.get(`/afromap/admin/photo-reports?status=open`); setPhotoReports(data.reports || []); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);

  const loadDashboard = useCallback(async () => {
    try { const { data } = await api.get(`/afromap/admin/dashboard`); setDash(data); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);

  useEffect(() => {
    if (view === "places") load();
    else if (view === "reviews") loadReviews();
    else if (view === "photos") loadPhotoReports();
    else if (view === "dashboard") loadDashboard();
  }, [view, load, loadReviews, loadPhotoReports, loadDashboard]);
  useEffect(() => { api.get("/afromap/categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {}); }, []);
  useEffect(() => { api.get(`/afromap/admin/stats`).then(({ data }) => setStats(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/afromap/admin/geocode-status").then(({ data }) => { setGeo(data); if (data.running) pollGeo(); }).catch(() => {}); }, [pollGeo]);

  const patch = async (id, body, msg) => {
    try { await api.patch(`/afromap/admin/places/${id}`, body); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer définitivement ce lieu ?")) return;
    try { await api.delete(`/afromap/admin/places/${id}`); toast.success("Lieu supprimé."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const moderateReview = async (id, action) => {
    try { await api.patch(`/afromap/admin/reviews/${id}?action=${action}`); toast.success(action === "remove" ? "Avis retiré." : "Avis rétabli."); loadReviews(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const moderatePhoto = async (id, action) => {
    try { await api.post(`/afromap/admin/photo-reports/${id}/resolve?action=${action}`); toast.success(action === "remove" ? "Image supprimée." : "Signalement ignoré."); loadPhotoReports(); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-afromap">
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setView("dashboard")} data-testid="afro-admin-view-dashboard"
          className={`text-sm px-4 py-1.5 rounded-full border inline-flex items-center gap-1.5 ${view === "dashboard" ? "gold-gradient text-black border-transparent" : "border-white/15 text-white/60"}`}>
          <BarChart3 size={14} /> Tableau de bord
        </button>
        <button onClick={() => setView("places")} data-testid="afro-admin-view-places"
          className={`text-sm px-4 py-1.5 rounded-full border ${view === "places" ? "gold-gradient text-black border-transparent" : "border-white/15 text-white/60"}`}>Lieux</button>
        <button onClick={() => setView("reviews")} data-testid="afro-admin-view-reviews"
          className={`text-sm px-4 py-1.5 rounded-full border ${view === "reviews" ? "gold-gradient text-black border-transparent" : "border-white/15 text-white/60"}`}>
          Avis signalés {stats.reported_reviews ? `(${stats.reported_reviews})` : ""}
        </button>
        <button onClick={() => setView("photos")} data-testid="afro-admin-view-photos"
          className={`text-sm px-4 py-1.5 rounded-full border ${view === "photos" ? "gold-gradient text-black border-transparent" : "border-white/15 text-white/60"}`}>
          Photos signalées {stats.reported_photos ? `(${stats.reported_photos})` : ""}
        </button>
      </div>

      {view === "dashboard" ? (
        <div className="space-y-4" data-testid="afro-dashboard">
          {!dash ? <p className="text-white/40 text-sm">Chargement…</p> : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="afro-dash-totals">
                <StatCard icon={Eye} label="Vues" value={dash.totals.views} />
                <StatCard icon={Phone} label="Clics téléphone" value={dash.totals.calls} />
                <StatCard icon={Globe} label="Clics site web" value={dash.totals.website} />
                <StatCard icon={Navigation} label="Itinéraires" value={dash.totals.route} />
                <StatCard icon={Star} label="Clics réseaux" value={dash.totals.clicks} />
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-white/50">
                <span className="card-surface px-3 py-1.5 rounded-full">{dash.counts.approved} lieux publiés</span>
                <span className="card-surface px-3 py-1.5 rounded-full text-yellow-300">{dash.counts.pending} en attente</span>
                <span className="card-surface px-3 py-1.5 rounded-full text-emerald-300">{dash.counts.verified} vérifiés</span>
                <span className="card-surface px-3 py-1.5 rounded-full text-gold">{dash.counts.partner} partenaires</span>
              </div>
              <div className="card-surface p-4">
                <h4 className="font-medium text-sm mb-3 flex items-center gap-2"><BarChart3 size={15} className="text-gold" /> Lieux les plus consultés</h4>
                {dash.top_places.filter((p) => (p.stats?.views || 0) > 0).length === 0 ? (
                  <p className="text-white/35 text-sm">Aucune vue enregistrée pour le moment. Les statistiques se rempliront dès que les visiteurs consulteront les fiches.</p>
                ) : (
                  <div className="space-y-1.5" data-testid="afro-dash-top">
                    {dash.top_places.map((p, i) => (
                      <div key={p.place_id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                        <span className="text-gold/70 text-sm w-5 text-center">{i + 1}</span>
                        {(p.photos?.[0] || p.logo)
                          ? <img src={photoUrl(p.photos?.[0] || p.logo)} alt="" className="h-9 w-9 rounded-lg object-cover" />
                          : <div className="h-9 w-9 rounded-lg bg-white/[0.05] flex items-center justify-center text-white/30"><MapPin size={14} /></div>}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate flex items-center gap-1.5">{p.name}
                            {p.verified && <span className="text-[9px] text-emerald-300">✓</span>}
                            {p.partner && <span className="text-[9px] text-gold">★</span>}
                          </p>
                          <p className="text-white/35 text-xs truncate">{p.city || "—"}</p>
                        </div>
                        <div className="flex gap-3 text-xs text-white/50 shrink-0">
                          <span className="inline-flex items-center gap-1"><Eye size={12} /> {p.stats?.views || 0}</span>
                          <span className="inline-flex items-center gap-1"><Phone size={12} /> {p.stats?.calls || 0}</span>
                          <span className="inline-flex items-center gap-1"><Navigation size={12} /> {p.stats?.route || 0}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : view === "photos" ? (
        <div className="space-y-3" data-testid="afro-photo-reports">
          {photoReports.length === 0 && <p className="text-white/30 text-sm">Aucune image signalée.</p>}
          {photoReports.map((r) => (
            <div key={r.report_id} className="card-surface p-4 flex gap-3 items-start" data-testid={`afro-photo-mod-${r.report_id}`}>
              <img src={photoUrl(r.photo)} alt="" className="h-20 w-20 rounded-lg object-cover shrink-0 border border-white/10" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Flag size={14} className="text-red-400" />
                  <span className="text-white/40 text-xs">sur « {r.place_name || "—"} »</span>
                </div>
                {r.reason && <p className="text-white/60 text-sm mt-1">Motif : {r.reason}</p>}
                <div className="flex gap-2 mt-3">
                  <Act onClick={() => moderatePhoto(r.report_id, "remove")} testid={`afro-photo-remove-${r.report_id}`} icon={Trash2} label="Supprimer l'image" tone="red" />
                  <Act onClick={() => moderatePhoto(r.report_id, "dismiss")} testid={`afro-photo-dismiss-${r.report_id}`} icon={RotateCcw} label="Ignorer" tone="green" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : view === "reviews" ? (
        <div className="space-y-3">
          {reviews.length === 0 && <p className="text-white/30 text-sm">Aucun avis signalé.</p>}
          {reviews.map((r) => (
            <div key={r.review_id} className="card-surface p-4" data-testid={`afro-review-mod-${r.review_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Flag size={14} className="text-red-400" />
                <span className="font-medium">{r.user_name}</span>
                <span className="text-white/40 text-xs">sur « {r.place_name} »</span>
                <span className="text-gold text-xs">{r.rating}/5</span>
                <span className="text-red-300 text-xs">{r.reports_count} signalement(s)</span>
              </div>
              {r.comment && <p className="text-white/60 text-sm mt-1">{r.comment}</p>}
              <div className="flex gap-2 mt-3">
                <Act onClick={() => moderateReview(r.review_id, "remove")} testid={`afro-review-remove-${r.review_id}`} icon={Trash2} label="Retirer l'avis" tone="red" />
                <Act onClick={() => moderateReview(r.review_id, "restore")} testid={`afro-review-restore-${r.review_id}`} icon={RotateCcw} label="Rétablir" tone="green" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="card-surface p-4" data-testid="afro-csv-toolbar">
            <div className="flex items-center gap-2 mb-2">
              <FileSpreadsheet size={16} className="text-gold" />
              <h4 className="font-medium text-sm">Import / Export en masse (CSV)</h4>
            </div>
            <p className="text-white/45 text-xs mb-3">Ajoutez ou mettez à jour des centaines de lieux d'un coup. Colonnes : nom, catégorie, description, adresse, ville, code postal, département, région, téléphone, e-mail, site web, instagram, facebook, horaires, photo principale, galerie (séparée par « | »), lat, lng, mise à la une (oui/non), événement, tags. Astuce : incluez les colonnes lat/lng pour un affichage immédiat sur la carte.</p>
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-gold/40 text-gold hover:bg-gold/10 cursor-pointer transition-colors" data-testid="afro-csv-import">
                {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {importing ? "Import en cours…" : "Importer un CSV"}
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={importCsv} disabled={importing} />
              </label>
              <button onClick={() => downloadCsv("/afromap/admin/export-csv?status=all", "carte-afro-lieux.csv")} data-testid="afro-csv-export"
                className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors">
                <Download size={14} /> Exporter tous les lieux
              </button>
              <button onClick={() => downloadCsv("/afromap/admin/import-template", "modele-import-carte-afro.csv")} data-testid="afro-csv-template"
                className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors">
                <FileSpreadsheet size={14} /> Télécharger le modèle
              </button>
              <button onClick={() => downloadCsv("/afromap/admin/sample-csv", "exemple-carte-afro.csv")} data-testid="afro-csv-sample"
                className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-gold/30 text-gold hover:bg-gold/10 transition-colors">
                <Download size={14} /> Exemple prérempli (8 lieux)
              </button>
              <button onClick={geocodeMissing} disabled={geo.running} data-testid="afro-geocode-missing"
                className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors disabled:opacity-60">
                {geo.running ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
                {geo.running ? `Géocodage… ${geo.done}/${geo.total}` : `Géolocaliser les lieux sans GPS${geo.remaining ? ` (${geo.remaining})` : ""}`}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {[["pending", "À valider", stats.pending], ["approved", "Publiés", stats.approved], ["rejected", "Refusés", stats.rejected], ["archived", "Archivés", stats.archived], ["all", "Tous", stats.total]].map(([s, lbl, n]) => (
              <button key={s} onClick={() => setStatus(s)} data-testid={`afro-admin-tab-${s}`}
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${status === s ? "gold-gradient text-black border-transparent" : "border-white/15 text-white/60 hover:text-white"}`}>
                {lbl} ({n || 0})
              </button>
            ))}
            <GenerateButton data-testid="afro-admin-add" onClick={() => setEditing({ ...EMPTY })} className="ml-auto"><Plus size={16} /> Ajouter un lieu</GenerateButton>
          </div>

          <div className="space-y-3">
            {places.map((p) => (
              <div key={p.place_id} data-testid={`afro-admin-place-${p.place_id}`} className="card-surface p-4">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  {p.photos?.[0] && <img src={photoUrl(p.photos[0])} alt="" className="h-16 w-16 rounded-lg object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{p.name}</span>
                      <span className="text-[11px] uppercase tracking-wide text-gold border border-gold/30 rounded-full px-2 py-0.5">{catLabel(p.category)}</span>
                      {p.featured && <span className="text-[11px] text-gold">★ à la une</span>}
                      {p.pinned && <span className="text-[11px] text-gold">📌 épinglé</span>}
                      {p.verified && <span className="text-[11px] text-emerald-300 border border-emerald-400/30 rounded-full px-1.5">✓ Vérifié</span>}
                      {p.partner && <span className="text-[11px] text-gold border border-gold/40 rounded-full px-1.5">★ Partenaire</span>}
                      {p.remote && <span className="text-[11px] text-sky-300 border border-sky-400/30 rounded-full px-1.5">💻 À distance</span>}
                      <span className={`text-[11px] ${p.status === "approved" ? "text-green-400" : p.status === "rejected" ? "text-red-400" : p.status === "archived" ? "text-white/40" : "text-yellow-400"}`}>{p.status}</span>
                      {p.possible_duplicate_of?.length > 0 && <span className="text-[11px] text-yellow-400">⚠ doublon possible</span>}
                    </div>
                    <p className="text-white/40 text-xs mt-0.5 flex items-center gap-1">
                      <MapPin size={11} /> {[p.address, p.postal_code, p.city].filter(Boolean).join(", ") || "—"}{p.department ? ` (${p.department})` : ""}
                      {p.lat == null && <span className="text-red-400 ml-1">(non géolocalisé)</span>}
                    </p>
                    {p.description && <p className="text-white/55 text-sm mt-1 line-clamp-2">{p.description}</p>}
                    {p.stats && <p className="text-white/30 text-[11px] mt-1" data-testid={`afro-stats-${p.place_id}`}>👁 {p.stats.views || 0} vues · 📞 {p.stats.calls || 0} appels · 🌐 {p.stats.website || 0} site · 🧭 {p.stats.route || 0} itinéraires</p>}
                    <p className="text-white/25 text-[11px] mt-1">{p.source === "community" ? `Proposé par ${p.submitter_email || "un membre"}` : "Ajouté par l'admin"}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {p.status !== "approved" && <Act onClick={() => patch(p.place_id, { status: "approved" }, "Lieu publié.")} testid={`afro-approve-${p.place_id}`} icon={Check} label="Approuver" tone="green" />}
                  {p.status !== "rejected" && <Act onClick={() => { const r = window.prompt("Motif du refus (optionnel) :") ?? ""; patch(p.place_id, { status: "rejected", reject_reason: r }, "Lieu refusé."); }} testid={`afro-reject-${p.place_id}`} icon={X} label="Refuser" tone="red" />}
                  {p.status !== "archived" && <Act onClick={() => patch(p.place_id, { status: "archived" }, "Lieu archivé.")} testid={`afro-archive-${p.place_id}`} icon={Archive} label="Archiver" />}
                  <Act onClick={() => patch(p.place_id, { featured: !p.featured })} icon={Star} label={p.featured ? "Retirer une" : "Mettre à la une"} />
                  <Act onClick={() => patch(p.place_id, { verified: !p.verified }, p.verified ? "Badge Vérifié retiré." : "Badge Vérifié ajouté.")} testid={`afro-verify-${p.place_id}`} icon={Check} label={p.verified ? "Retirer Vérifié" : "Vérifié"} tone={p.verified ? undefined : "green"} />
                  <Act onClick={() => patch(p.place_id, { partner: !p.partner }, p.partner ? "Badge Partenaire retiré." : "Badge Partenaire ajouté.")} testid={`afro-partner-${p.place_id}`} icon={Star} label={p.partner ? "Retirer Partenaire" : "Partenaire"} />
                  <Act onClick={() => patch(p.place_id, { remote: !p.remote }, p.remote ? "À distance retiré." : "Marqué à distance / visio.")} testid={`afro-remote-${p.place_id}`} icon={Globe} label={p.remote ? "Retirer à distance" : "À distance / visio"} />
                  <Act onClick={() => patch(p.place_id, { pinned: !p.pinned })} icon={Pin} label={p.pinned ? "Désépingler" : "Épingler"} />
                  <Act onClick={() => setEditing(p)} testid={`afro-edit-${p.place_id}`} icon={Pencil} label="Modifier" />
                  <Act onClick={() => setHistoryId(historyId === p.place_id ? null : p.place_id)} testid={`afro-history-${p.place_id}`} icon={History} label="Historique" />
                  <Act onClick={() => del(p.place_id)} testid={`afro-delete-${p.place_id}`} icon={Trash2} label="Supprimer" tone="red" />
                </div>
                {historyId === p.place_id && (
                  <div className="mt-3 pt-3 border-t border-white/10 text-xs space-y-1" data-testid={`afro-history-log-${p.place_id}`}>
                    {(p.moderation_log || []).length === 0 && <p className="text-white/30">Aucun historique.</p>}
                    {(p.moderation_log || []).map((m, i) => (
                      <p key={i} className="text-white/50"><span className="text-gold">{m.action}</span> — {m.by} · {new Date(m.at).toLocaleString("fr-FR")}{m.reason ? ` · ${m.reason}` : ""}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {places.length === 0 && <p className="text-white/30 text-sm">Aucun lieu dans cette catégorie.</p>}
          </div>
        </>
      )}

      {editing && <EditModal init={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

const Act = ({ onClick, icon: Icon, label, tone, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
      tone === "green" ? "border-green-500/30 text-green-300 hover:bg-green-500/10"
        : tone === "red" ? "border-red-500/30 text-red-300 hover:bg-red-500/10"
          : "border-white/15 text-white/70 hover:text-white hover:bg-white/[0.04]"}`}>
    <Icon size={14} /> {label}
  </button>
);

function EditModal({ init, categories, onClose, onSaved }) {
  const isNew = !init.place_id;
  const [form, setForm] = useState({
    ...EMPTY, ...init,
    email: init.email || "",
    instagram: init.socials?.instagram || "",
    facebook: init.socials?.facebook || "",
    tags: Array.isArray(init.tags) ? init.tags.join(", ") : (init.tags || ""),
    event: init.event || "",
    lat: init.lat ?? "", lng: init.lng ?? "",
  });
  const [photos, setPhotos] = useState(Array.isArray(init.photos) ? init.photos : []);
  const [logo, setLogo] = useState(init.logo || "");
  const [upP, setUpP] = useState(false);
  const [upL, setUpL] = useState(false);
  const dragIdx = useRef(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUpP(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/afromap/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((prev) => [...prev, data.path]);
      }
    } catch (err) { toast.error(apiErr(err)); } finally { setUpP(false); e.target.value = ""; }
  };
  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUpL(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/afromap/upload?kind=logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(data.path);
    } catch (err) { toast.error(apiErr(err)); } finally { setUpL(false); e.target.value = ""; }
  };
  const removePhoto = (i) => setPhotos((p) => p.filter((_, x) => x !== i));
  const makeCover = (i) => setPhotos((p) => { const a = [...p]; const [x] = a.splice(i, 1); a.unshift(x); return a; });
  const reorder = (to) => {
    const from = dragIdx.current;
    if (from == null || from === to) return;
    setPhotos((p) => { const a = [...p]; const [x] = a.splice(from, 1); a.splice(to, 0, x); return a; });
    dragIdx.current = null;
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nom requis.");
    setBusy(true);
    const body = { ...form };
    body.socials = {};
    if (form.instagram) body.socials.instagram = form.instagram;
    if (form.facebook) body.socials.facebook = form.facebook;
    delete body.instagram; delete body.facebook;
    body.tags = (form.tags || "").split(",").map((t) => t.trim()).filter(Boolean);
    body.photos = photos;
    body.logo = logo;
    body.verified = !!form.verified;
    body.partner = !!form.partner;
    body.remote = !!form.remote;
    delete body.stats;
    delete body.moderation_log; delete body.possible_duplicate_of; delete body.rating; delete body.rating_count;
    body.lat = form.lat === "" ? null : parseFloat(form.lat);
    body.lng = form.lng === "" ? null : parseFloat(form.lng);
    if (body.lat == null) { delete body.lat; delete body.lng; }
    try {
      if (isNew) { await api.post("/afromap/admin/places", body); toast.success("Lieu ajouté et publié."); }
      else { await api.patch(`/afromap/admin/places/${init.place_id}`, body); toast.success("Lieu mis à jour."); }
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center p-4" data-testid="afro-admin-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-gold/25 p-6" style={{ background: "#0A0A0A" }}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={20} /></button>
        <h3 className="font-display text-lg mb-4">{isNew ? "Ajouter un lieu" : "Modifier le lieu"}</h3>
        <div className="space-y-3">
          <input data-testid="afro-edit-name" placeholder="Nom *" value={form.name} onChange={set("name")} className={inputCls} />
          <select data-testid="afro-edit-category" value={form.category} onChange={set("category")} className={inputCls}>
            {categories.map((c) => <option key={c.key} value={c.key} className="bg-[#0A0A0A]">{c.label}</option>)}
          </select>
          <textarea placeholder="Description" rows={3} value={form.description} onChange={set("description")} className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Adresse" value={form.address} onChange={set("address")} className={inputCls} />
            <input placeholder="Ville" value={form.city} onChange={set("city")} className={inputCls} />
            <input placeholder="Code postal" value={form.postal_code} onChange={set("postal_code")} className={inputCls} />
            <input placeholder="Pays (FR, BE…)" value={form.country} onChange={set("country")} className={inputCls} />
            <input placeholder="Téléphone" value={form.phone} onChange={set("phone")} className={inputCls} />
            <input placeholder="Horaires" value={form.hours} onChange={set("hours")} className={inputCls} />
          </div>
          <input placeholder="E-mail" value={form.email} onChange={set("email")} className={inputCls} />
          <input placeholder="Site internet" value={form.website} onChange={set("website")} className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Instagram (URL)" value={form.instagram} onChange={set("instagram")} className={inputCls} />
            <input placeholder="Facebook (URL)" value={form.facebook} onChange={set("facebook")} className={inputCls} />
          </div>
          <input placeholder="Tags / mots-clés (séparés par des virgules)" value={form.tags} onChange={set("tags")} className={inputCls} />
          <input placeholder="Événement associé (optionnel)" value={form.event} onChange={set("event")} className={inputCls} />

          {/* Photos : glisser-déposer pour réordonner, 1re = couverture */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-white/60 text-xs">Photos <span className="text-white/30">(la 1re est la couverture · glissez pour réordonner)</span></label>
              <label className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border border-gold/40 text-gold hover:bg-gold/10 cursor-pointer transition-colors" data-testid="afro-edit-add-photos">
                {upP ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {upP ? "Envoi…" : "Ajouter"}
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadPhotos} disabled={upP} />
              </label>
            </div>
            {photos.length === 0 ? (
              <p className="text-white/30 text-[11px]">Aucune photo. Ajoutez-en plusieurs (objectif : 5+) pour une fiche attrayante.</p>
            ) : (
              <div className="grid grid-cols-4 gap-2" data-testid="afro-edit-photos">
                {photos.map((p, i) => (
                  <div key={p + i} draggable
                    onDragStart={() => { dragIdx.current = i; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => reorder(i)}
                    className={`relative group rounded-lg overflow-hidden border ${i === 0 ? "border-gold" : "border-white/10"} aspect-square cursor-move`}>
                    <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
                    {i === 0 && <span className="absolute bottom-0 inset-x-0 bg-gold/90 text-black text-[9px] text-center py-0.5 font-medium">Couverture</span>}
                    <div className="absolute top-0.5 right-0.5 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      {i !== 0 && (
                        <button type="button" onClick={() => makeCover(i)} title="Définir comme couverture"
                          className="w-5 h-5 rounded bg-black/70 text-gold flex items-center justify-center hover:bg-black"><Star size={11} /></button>
                      )}
                      <button type="button" onClick={() => removePhoto(i)} title="Supprimer"
                        data-testid={`afro-edit-photo-del-${i}`}
                        className="w-5 h-5 rounded bg-black/70 text-red-300 flex items-center justify-center hover:bg-black"><Trash2 size={11} /></button>
                    </div>
                    <GripVertical size={12} className="absolute top-1 left-1 text-white/50" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Logo (facultatif) */}
          <div>
            <label className="text-white/60 text-xs block mb-1.5">Logo de l'établissement <span className="text-white/30">(facultatif)</span></label>
            <div className="flex items-center gap-3">
              {logo ? (
                <div className="relative">
                  <img src={photoUrl(logo)} alt="logo" className="h-14 w-14 rounded-lg object-contain bg-white/[0.06] border border-white/10" />
                  <button type="button" onClick={() => setLogo("")} title="Retirer le logo"
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black border border-white/20 text-red-300 flex items-center justify-center"><X size={11} /></button>
                </div>
              ) : (
                <div className="h-14 w-14 rounded-lg bg-white/[0.04] border border-dashed border-white/15 flex items-center justify-center text-white/30"><ImageIcon size={18} /></div>
              )}
              <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/70 hover:text-white cursor-pointer transition-colors" data-testid="afro-edit-add-logo">
                {upL ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {upL ? "Envoi…" : (logo ? "Remplacer" : "Ajouter un logo")}
                <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} disabled={upL} />
              </label>
            </div>
          </div>

          {/* Badges de confiance */}
          <div className="flex gap-2">
            <button type="button" data-testid="afro-edit-verified"
              onClick={() => setForm({ ...form, verified: !form.verified })}
              className={`flex-1 text-xs px-3 py-2 rounded-xl border transition-colors ${form.verified ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-300" : "border-white/15 text-white/50 hover:text-white"}`}>
              {form.verified ? "✓ Vérifié (activé)" : "Marquer comme Vérifié"}
            </button>
            <button type="button" data-testid="afro-edit-partner"
              onClick={() => setForm({ ...form, partner: !form.partner })}
              className={`flex-1 text-xs px-3 py-2 rounded-xl border transition-colors ${form.partner ? "border-gold/50 bg-gold/10 text-gold" : "border-white/15 text-white/50 hover:text-white"}`}>
              {form.partner ? "★ Partenaire (activé)" : "Marquer comme Partenaire"}
            </button>
          </div>

          {/* Disponibilité à distance / visioconférence */}
          <button type="button" data-testid="afro-edit-remote"
            onClick={() => setForm({ ...form, remote: !form.remote })}
            className={`w-full text-xs px-3 py-2 rounded-xl border transition-colors ${form.remote ? "border-sky-400/50 bg-sky-400/10 text-sky-300" : "border-white/15 text-white/50 hover:text-white"}`}>
            {form.remote ? "💻 Disponible à distance / visio (activé)" : "Marquer comme disponible à distance / visio"}
          </button>

          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Latitude (auto si vide)" value={form.lat} onChange={set("lat")} className={inputCls} />
            <input placeholder="Longitude (auto si vide)" value={form.lng} onChange={set("lng")} className={inputCls} />
          </div>
          <p className="text-white/30 text-[11px]">Laissez lat/lng vides pour un géocodage automatique de l'adresse (OpenStreetMap).</p>
        </div>
        <button onClick={save} disabled={busy} data-testid="afro-edit-save"
          className="w-full mt-5 inline-flex items-center justify-center gap-2 gold-gradient text-black rounded-full py-2.5 font-medium disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} {isNew ? "Ajouter" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}
